// File: src/components/season/SeasonDetailPage.js
import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import AddTeamModal from "../teams/AddTeamModal";
import AddLocationModal from "./AddLocationModal";
import AddGameModal from "../games/AddGameModal";
import useUserRoles from '../../hooks/useUserRoles';

function SeasonDetailPage({ seasonId: propSeasonId, onBack }) {
	const { isCommissioner } = useUserRoles();

	// Use URL param or prop for seasonId
	const { seasonId: paramSeasonId } = useParams();
	const [searchParams] = useSearchParams(); // Get query parameters
	const navigate = useNavigate();

	const seasonId = propSeasonId || paramSeasonId;

	// State for API data, inner tab control, etc.
	const [teams, setTeams] = useState([]);
	const [games, setGames] = useState([]);
	const [seasonDetails, setSeasonDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	// const [isChecked, setIsChecked] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);

	const [showLocationModal, setShowLocationModal] = useState(false);

	const [showGameModal, setShowGameModal] = useState(false);

	// Controlled state for inner tab navigation; defaults to "Teams"
	const [innerActiveTab, setInnerActiveTab] = useState("Teams");




	// Get the is_played value from query params or default to false
	const isPlayed = searchParams.get('is_played') === 'true';

	useEffect(() => {
		// Fetch games for the league
		axios
			.get(`/api/games/seasons/${seasonId}/`, {
				params: { is_played: isPlayed }, // Pass is_played dynamically
				headers: {
					Authorization: `Bearer ${localStorage.getItem('access_token')}`,
				},
			})
			.then((response) => setGames(response.data))
			.catch((error) => {
				console.error('Error fetching games:', error);
				alert('Unable to fetch games.');
			});
	}, [seasonId, isPlayed]);

	useEffect(() => {
		axios
			.get(`/api/leagues/seasons/${seasonId}/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("access_token")}`,
				},
			})
			.then((response) => {
				setSeasonDetails(response.data);
				setLoading(false);
			})
			.catch((error) => {
				console.error("Error fetching season details:", error);
				setError("Failed to load season details.");
				setLoading(false);
			});

		axios
			.get(`/api/teams/${seasonId}/teams/`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("access_token")}`,
				},
			})
			.then((response) => {
				setTeams(response.data);
			})
			.catch((error) => {
				console.error("Error fetching teams:", error);
				alert("Unable to fetch teams.");
			});
	}, [seasonId]);

	if (loading) {
		return <div className="container">Loading season details...</div>;
	}
	if (error) {
		return <div className="container text-danger">{error}</div>;
	}

	const handleShow = () => setModalVisible(true);
	const handleClose = () => setModalVisible(false);
	// const handleCheckboxChange = () => setIsChecked(!isChecked);

	const handleShowLocationModal = () => setShowLocationModal(true);
	const handleCloseLocationModal = () => setShowLocationModal(false);

	const handleShowGameModal = () => setShowGameModal(true);
	const handleCloseGameModal = () => setShowGameModal(false);

	const handleLocationAdded = (newLocation) => {
		setSeasonDetails((prev) => ({
			...prev,
			locations: prev.locations ? [...prev.locations, newLocation] : [newLocation],
		}));
	};

	// Navigate back to Settings with the Leagues tab active.
	const handleBack = () => navigate("/settings?active=6");

	const handleTeamAdded = (newTeam) => {
		setTeams((prevTeams) => [...prevTeams, newTeam]);
	};

	const handleGameAdded = (newGame) => {
		setGames((prevGames) => [...prevGames, newGame]);
	};

	const renderCreateButton = () => {
		if (!isCommissioner) return null;

		switch (innerActiveTab) {
			case "Teams":
				return (
					<button className="btn btn-primary-soft" onClick={handleShow}>
						<i className="fa-solid fa-plus pe-1"></i> Create Team
					</button>
				);
			case "Schedule":
				return (
					<button
						className="btn btn-primary-soft"
						onClick={handleShowGameModal}
					>
						<i className="fa-solid fa-plus pe-1"></i> Create Schedule
					</button>
				);
			case "Events":
				return (
					<button
						className="btn btn-primary-soft"
						data-bs-toggle="modal"
						data-bs-target="#modalCreateEvents"
					>
						<i className="fa-solid fa-plus pe-1"></i> Create Event
					</button>
				);
			default:
				return null;
		}
	};

	return (
		// Render this page as the Leagues tab pane in your SettingsLayout (id="nav-setting-tab-6")
		<div className="tab-pane fade show active" id="nav-setting-tab-6">
			<div className="container">

				{/* Season Detail Header */}
				<div className="container">
					<div className="card mb-3 p-0">
						<div
							className="h-200px rounded-top"
							style={{
								backgroundImage: `url(${seasonDetails.banner ||
									`${process.env.PUBLIC_URL}/images/kb_static/default_season_banner.png`
									})`,
								backgroundPosition: "center",
								backgroundSize: "cover",
								backgroundRepeat: "no-repeat",
							}}
						></div>
					</div>
				</div>

				{/* Season Details */}
				<div className="card card-body mb-3">
					<div className="row g-4">
						<div className="col-sm-6 col-lg-4">
							<h5>Start Date</h5>
							<p className="small mb-0">{seasonDetails.start_date}</p>
						</div>
						<div className="col-sm-6 col-lg-4">
							<h5>End Date</h5>
							<p className="small mb-0">{seasonDetails.end_date}</p>
						</div>
						<div className="col-sm-6 col-lg-4">
							<h5>League</h5>
							<p className="small mb-0">
								<button
									className="btn btn-link text-decoration-none p-0"
									onClick={handleBack}
								>
									{seasonDetails.league_name}
								</button>
							</p>
						</div>
						<div className="col-sm-6 col-lg-4">
							<h5>Fields</h5>
							{seasonDetails.locations && seasonDetails.locations.length > 0 ? (
								<ul className="list-unstyled">
									{seasonDetails.locations.map((location) => (
										<li key={location.id}>
											{location.name}, {location.city}
										</li>
									))}
								</ul>
							) : (
								<p className="small mb-0">No locations available</p>
							)}
							{isCommissioner && (
								<button className="btn btn-primary mt-2" onClick={handleShowLocationModal}>
									Add Location
								</button>
							)}
						</div>
					</div>
				</div>

				{/* Inner Tab Navigation */}
				<div className="card h-100">
					<div className="card-header d-sm-flex align-items-center text-center justify-content-sm-between border-0 pb-0">
						<h1 className="h4 card-title">Season Details</h1>
						{renderCreateButton()}
					</div>

					<div className="card-body">
						<ul className="nav nav-tabs nav-bottom-line justify-content-center justify-content-md-start">
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Teams" ? "active" : ""}`}
									href="#tab-1"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Teams");
									}}
								>
									Teams
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Schedule" ? "active" : ""}`}
									href="#tab-2"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Schedule");
									}}
								>
									Schedule
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Players" ? "active" : ""}`}
									href="#tab-3"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Players");
									}}
								>
									Players
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Events" ? "active" : ""}`}
									href="#tab-4"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Events");
									}}
								>
									Events
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Standings" ? "active" : ""}`}
									href="#tab-5"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Standings");
									}}
								>
									Standings
								</a>
							</li>
							<li className="nav-item">
								<a
									className={`nav-link ${innerActiveTab === "Rules" ? "active" : ""}`}
									href="#tab-6"
									onClick={(e) => {
										e.preventDefault();
										setInnerActiveTab("Rules");
									}}
								>
									Rules
								</a>
							</li>
						</ul>

						{/* Inner Tab Content */}
						<div className="tab-content mb-0 pb-0">
							<div
								className={`tab-pane fade ${innerActiveTab === "Teams" ? "show active" : ""}`}
								id="tab-1"
							>
								<div className="container">
									<div className="row g-4">
										{teams.length > 0 ? (
											teams.map((team) => (
												<div key={team.id} className="col-sm-6 col-xl-4">
													<Link to={`/teams/${team.id}`} className="text-decoration-none">
														<div className="card h-100">
															<div className="position-relative">
																<img
																	className="img-fluid rounded-top"
																	src={
																		team.logo ||
																		`${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`
																	}
																	alt={`${team.name} logo`}
																	style={{
																		width: "200px",
																		height: "200px",
																		objectFit: "cover",
																		margin: "auto",
																		display: "block",
																	}}
																/>
															</div>
															<div className="card-body position-relative pt-0">
																<h6 className="mt-3 text-dark">{team.name}</h6>
																<p className="mb-0 small text-muted">
																	<i className="bi bi-trophy pe-1"></i> Wins: {team.wins}
																</p>
																<p className="small text-muted">
																	<i className="bi bi-trophy-fill pe-1"></i> Losses: {team.losses}
																</p>
																<p className="small text-muted">
																	<i className="bi bi-trophy-fill pe-1"></i> Draws: {team.draws}
																</p>
															</div>
														</div>
													</Link>
												</div>
											))
										) : (
											<div className="col-12">
												<p>No teams found.</p>
											</div>
										)}
									</div>
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Schedule" ? "show active" : ""}`}
								id="tab-2"
							>
								<div className="container">
									<div className="row g-4">
										{games.map((game) => {
											const homeParticipant = game.participants?.find((p) => p.is_home) || {};
											const awayParticipant = game.participants?.find((p) => p.is_away) || {};

											return (
												<div key={game.id} className="col-sm-6 col-xl-4">
													{/* Make the entire card clickable */}
													<Link to={`/games/${game.id}`} className="text-decoration-none">
														<div className="card h-100">
															<div className="position-relative">
																<div
																	style={{
																		width: '200px',
																		height: '200px',
																		margin: 'auto',
																		display: 'block',
																		position: 'relative',
																		overflow: 'hidden',
																		borderRadius: '8px',
																	}}
																>
																	{homeParticipant.participant_logo ? (
																		// Top half - Home Team logo
																		<img
																			src={homeParticipant.participant_logo}
																			alt={`${homeParticipant.participant_name} logo`}
																			style={{
																				width: '200px',
																				height: '200px',
																				objectFit: 'cover',
																				position: 'absolute',
																				left: 0, // Ensure image is aligned to the left
																				top: 0,
																				clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
																			}}
																		/>
																	) : (
																		// Placeholder for top half - Blue
																		<div
																			style={{
																				width: '100%',
																				height: '50%',
																				backgroundColor: 'blue',
																				position: 'absolute',
																				top: 0,
																			}}
																		></div>
																	)}

																	{awayParticipant.participant_logo ? (
																		// Bottom half - Away Team logo
																		<img
																			src={awayParticipant.participant_logo}
																			alt={`${awayParticipant.participant_name} logo`}
																			style={{
																				width: '200px',
																				height: '200px',
																				objectFit: 'cover',
																				position: 'absolute',
																				left: 0, // Ensure image is aligned to the left
																				bottom: 0,
																				clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
																			}}
																		/>
																	) : (
																		// Placeholder for bottom half - Red
																		<div
																			style={{
																				width: '100%',
																				height: '50%',
																				backgroundColor: 'red',
																				position: 'absolute',
																				bottom: 0,
																			}}
																		></div>
																	)}
																</div>
															</div>
															<div className="card-body position-relative pt-0">
																<div className="row">
																	<h6 className="mt-3 text-dark">
																		{homeParticipant.participant_name || 'Home Team'}
																	</h6>
																	<h6 className="mt-3 text-dark">
																		vs.{' '}
																	</h6>
																	<h6 className="mt-3 text-dark">
																		{awayParticipant.participant_name || 'Away Team'}
																	</h6>
																</div>
																<p className="mb-0 small text-muted">
																	<i className="bi bi-calendar-check pe-1"></i> Date: {game.scheduled_date_only}
																</p>
																<p className="small text-muted">
																	<i className="bi bi-clock pe-1"></i> Time: {game.scheduled_time_only}
																</p>
																<p className="small text-muted">
																	<i className="bi bi-geo-alt pe-1"></i> Location: {game.location.name}
																</p>
															</div>
														</div>
													</Link>
												</div>
											);
										})}
									</div>
								</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Players" ? "show active" : ""}`}
								id="tab-3"
							>
								<div>Players content goes here</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Events" ? "show active" : ""}`}
								id="tab-4"
							>
								<div>Events content goes here</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Standings" ? "show active" : ""}`}
								id="tab-5"
							>
								<div>Standings content goes here</div>
							</div>
							<div
								className={`tab-pane fade ${innerActiveTab === "Rules" ? "show active" : ""}`}
								id="tab-6"
							>
								<div>Rules content goes here</div>
							</div>
						</div>
					</div>
				</div>

				<AddTeamModal
					show={modalVisible}
					handleClose={handleClose}
					onTeamAdded={handleTeamAdded}
				/>
				<AddLocationModal
					show={showLocationModal}
					handleClose={handleCloseLocationModal}
					onLocationAdded={handleLocationAdded}
					seasonId={seasonId}
				/>
				<AddGameModal
					show={showGameModal}         // Assume you have state for controlling Game modal visibility
					handleClose={handleCloseGameModal}  // Your function to close the modal
					onGameAdded={handleGameAdded} // Callback to update the game list
					seasonId={seasonId}           // Pass the seasonId here
				/>
			</div>
		</div>
	);
}

export default SeasonDetailPage;
